import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import AnalysisProgress from "../components/AnalysisProgress";
import { 
  Globe, 
  ArrowRight, 
  CheckCircle, 
  Target, 
  BarChart3, 
  Loader2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Lock,
  Calendar,
  Mail,
  Phone,
  User,
  Building2,
  Sparkles,
  MessageSquare,
  Compass,
  Trophy
} from "lucide-react";
import { getScoreBand, getPercentileText } from "@/lib/scoreBands";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configuration - Update these for deployment
const CONFIG = {
  calendlyLink: "https://calendly.com/mark-igtms/gtm-clarity-mri-consultation",
  companyName: "Integrated Go-To-Market Solutions",
  companyWebsite: "https://igtms.com",
  supportEmail: "info@igtms.com",
  // Brand colors from IGTMS website
  brandGreen: "#07926b",
  brandDark: "#1a1a1a",
};

export default function PublicScore() {
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [jobId, setJobId] = useState(null);
  const [result, setResult] = useState(null);
  const [pollingStatus, setPollingStatus] = useState("");
  
  // Score prediction game
  const [predictedScore, setPredictedScore] = useState(null);
  const predictedScoreRef = useRef(null);
  const [analysisStartTime, setAnalysisStartTime] = useState(null);
  
  // Optional GTM alignment questions
  const [idealCustomer, setIdealCustomer] = useState("");
  const [primaryOffer, setPrimaryOffer] = useState("");
  const [leadSources, setLeadSources] = useState("");

  // Validate email as user types
  const validateEmail = async (emailValue, domain) => {
    if (!emailValue || !domain) {
      setEmailError("");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    const personalDomains = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com", "icloud.com", "mail.com", "protonmail.com"];
    const emailDomain = emailValue.split("@")[1]?.toLowerCase();
    if (personalDomains.includes(emailDomain)) {
      setEmailError("Please use your work email address");
      return;
    }

    setEmailError("");
  };

  useEffect(() => {
    if (email && websiteUrl) {
      const debounce = setTimeout(() => {
        validateEmail(email, websiteUrl);
      }, 500);
      return () => clearTimeout(debounce);
    }
  }, [email, websiteUrl]);

  // Normalize a US phone number to E.164 (+1XXXXXXXXXX). Returns null if invalid.
  const normalizePhone = (value) => {
    let cleaned = value.trim().replace(/[\s\-.()]/g, "");
    if (cleaned.startsWith("+")) cleaned = cleaned.slice(1);
    if (!/^\d+$/.test(cleaned)) return null;
    if (cleaned.length === 10) return `+1${cleaned}`;
    if (cleaned.length === 11 && cleaned.startsWith("1")) return `+${cleaned}`;
    return null;
  };

  // Validate phone as user types (required error only shows on submit)
  useEffect(() => {
    if (!phone.trim()) {
      setPhoneError("");
      return;
    }
    const debounce = setTimeout(() => {
      setPhoneError(normalizePhone(phone) ? "" : "Please enter a valid US phone number");
    }, 500);
    return () => clearTimeout(debounce);
  }, [phone]);

  // Poll for job results
  useEffect(() => {
    if (!jobId) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/jobs/${jobId}`);
        const job = response.data;

        setPollingStatus(job.current_step);

        if (job.status === "completed") {
          clearInterval(pollInterval);
          if (job.results && job.results[0]?.public_score_id) {
            const publicScoreId = job.results[0].public_score_id;
            const scoreResponse = await axios.get(`${API}/public-scores/${publicScoreId}`);
            setResult(scoreResponse.data.teaser_scores);
            
            // Save the prediction to the backend if user made one
            if (predictedScoreRef.current !== null) {
              try {
                await axios.patch(`${API}/public-scores/${publicScoreId}/prediction`, {
                  predicted_score: predictedScoreRef.current
                });
              } catch (err) {
                console.error("Failed to save prediction:", err);
              }
            }
          } else if (job.results && job.results[0]) {
            setResult({
              company_name: job.results[0].company_name,
              final_score: job.results[0].final_score,
              teaser: true,
              message: "Your Revenue Acceleration Score is ready! Book a call to get the full detailed report."
            });
          }
          setLoading(false);
        } else if (job.status === "error") {
          clearInterval(pollInterval);
          toast.error(job.results?.[0]?.error || "Analysis failed. Please try again.");
          setLoading(false);
          setJobId(null);
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [jobId]);

  // Hide platform badge and fix scrolling when embedded in iframe
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Hide Emergent platform badge */
      [class*="emergent"], 
      [id*="emergent"],
      a[href*="emergent"],
      div[style*="Made with"],
      iframe + div,
      body > div:last-child:not(#root) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      /* Fix for iframe embedding - prevent double scrollbar */
      html, body {
        margin: 0;
        padding: 0;
        overflow-x: hidden;
      }
      #root {
        min-height: 100%;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Normalize URL - add https:// if missing
  const normalizeUrl = (url) => {
    let normalized = url.trim();
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
      normalized = 'https://' + normalized;
    }
    return normalized;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!websiteUrl.trim()) {
      toast.error("Please enter your website URL");
      return;
    }

    if (!email.trim()) {
      toast.error("Please enter your work email");
      return;
    }

    if (emailError) {
      toast.error(emailError);
      return;
    }

    if (!phone.trim()) {
      setPhoneError("Phone number is required");
      toast.error("Please enter your phone number");
      return;
    }

    const normalizedPhone = normalizePhone(phone);
    if (!normalizedPhone) {
      setPhoneError("Please enter a valid US phone number");
      toast.error("Please enter a valid US phone number");
      return;
    }

    // Normalize the URL before sending
    const normalizedUrl = normalizeUrl(websiteUrl);

    setLoading(true);
    setResult(null);

    try {
      const validationResponse = await axios.post(`${API}/validate-email`, {
        email: email.trim(),
        domain: normalizedUrl
      });

      if (!validationResponse.data.valid) {
        toast.error(validationResponse.data.reason);
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/public/score`, {
        url: normalizedUrl,
        email: email.trim(),
        name: name.trim() || undefined,
        company_name: companyName.trim() || undefined,
        phone: normalizedPhone,
        // Optional GTM alignment questions
        ideal_customer: idealCustomer.trim() || undefined,
        primary_offer: primaryOffer.trim() || undefined,
        lead_sources: leadSources.trim() || undefined
      });

      setJobId(response.data.job_id);
      setAnalysisStartTime(Date.now());
      toast.success("Analyzing your website... This may take up to 3 minutes for deep research.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start analysis. Please try again.");
      setLoading(false);
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case "crawling": return "Scanning your website...";
      case "analyzing": return "Running AI analysis...";
      case "gathering_social_data": return "Checking social presence...";
      case "searching_external_signals": return "Researching external signals...";
      case "analyzing_with_gpt52": return "Deep analysis in progress...";
      case "analyzing_360": return "Performing deep analysis...";
      case "generating": return "Generating your report...";
      default: return "Processing...";
    }
  };

  // Check for embed mode via URL parameter OR iframe detection
  const urlParams = new URLSearchParams(window.location.search);
  const isEmbed = urlParams.get('embed') === 'true' || window.self !== window.top;
  const isInIframe = window.self !== window.top;

  // For embed mode: minimal UI, no extra sections
  if (isEmbed) {
    return (
      <div className="bg-[#232326]" style={{ minHeight: 'auto' }}>
        {/* Green accent bar */}
        <div className="h-1 w-full" style={{ backgroundColor: CONFIG.brandGreen }}></div>
        
        {/* Main Content - Compact */}
        <div className="bg-[#232326] py-8 px-4">
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-6">
              <h1 className="font-semibold text-2xl sm:text-3xl text-white mb-2">
                Revenue Acceleration Score
              </h1>
              <p className="text-sm text-slate-300">
                Get an instant AI-powered assessment of your GTM strategy
              </p>
            </div>

            {!result ? (
              loading && jobId ? (
                <AnalysisProgress
                  companyName={companyName || "your website"}
                  pollingStatus={pollingStatus}
                  onPrediction={(score) => { setPredictedScore(score); predictedScoreRef.current = score; }}
                  startTime={analysisStartTime}
                />
              ) : (
              <Card className="p-6 bg-white shadow-xl border-0">
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      <Globe className="w-4 h-4 inline mr-1 text-slate-400" />
                      Website URL
                    </label>
                    <Input
                      type="text"
                      placeholder="yourcompany.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      className="h-10"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      <Mail className="w-4 h-4 inline mr-1 text-slate-400" />
                      Work Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`h-10 ${emailError ? "border-red-500" : ""}`}
                      disabled={loading}
                    />
                    {emailError && (
                      <p className="mt-1 text-xs text-red-500">{emailError}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        <User className="w-4 h-4 inline mr-1 text-slate-400" />
                        Name
                      </label>
                      <Input
                        type="text"
                        placeholder="John Smith"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-10"
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        <Building2 className="w-4 h-4 inline mr-1 text-slate-400" />
                        Company
                      </label>
                      <Input
                        type="text"
                        placeholder="Acme Inc"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="h-10"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Phone Number - Required */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      <Phone className="w-4 h-4 inline mr-1 text-slate-400" />
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="tel"
                      required
                      placeholder="+1 (555) 123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={`h-10 ${phoneError ? "border-red-500" : ""}`}
                      disabled={loading}
                    />
                    {phoneError && (
                      <p className="mt-1 text-xs text-red-500">{phoneError}</p>
                    )}
                  </div>

                  {/* Optional GTM Alignment Questions */}
                  <div className="border-t border-slate-200 pt-3 mt-2">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          <Target className="w-4 h-4 inline mr-1 text-slate-400" />
                          Describe your ideal customer
                        </label>
                        <Textarea
                          placeholder="What characteristics would make them an instant 'yes' for your product or service?"
                          value={idealCustomer}
                          onChange={(e) => setIdealCustomer(e.target.value)}
                          className="min-h-[60px] text-sm resize-none"
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          <Sparkles className="w-4 h-4 inline mr-1 text-slate-400" />
                          Primary product or offer focus
                        </label>
                        <Textarea
                          placeholder="If you sell more than one thing, which product or offer do you want more customers choosing right now?"
                          value={primaryOffer}
                          onChange={(e) => setPrimaryOffer(e.target.value)}
                          className="min-h-[60px] text-sm resize-none"
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          <Compass className="w-4 h-4 inline mr-1 text-slate-400" />
                          Current lead sources
                        </label>
                        <Textarea
                          placeholder="Where are most of your leads currently coming from?"
                          value={leadSources}
                          onChange={(e) => setLeadSources(e.target.value)}
                          className="min-h-[60px] text-sm resize-none"
                          disabled={loading}
                        />
                      </div>

                      <p className="text-xs text-slate-500 italic">
                        These questions are optional but help us provide deeper insights by comparing your internal GTM perspective with how your market positioning is perceived externally.
                      </p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !!emailError || !!phoneError}
                    className="w-full h-11 text-white font-medium"
                    style={{ backgroundColor: CONFIG.brandGreen }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {getStatusMessage(pollingStatus)}
                      </>
                    ) : (
                      <>
                        Get My Free Score
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-slate-500">
                    We analyze publicly available information. No login required.
                  </p>
                  <p className="text-xs text-center text-slate-400 mt-1">
                    This tool does deep research to ensure accuracy. Please allow up to 3 minutes for your score to be calculated. We will also send a copy of the results to the email you provided.
                  </p>
                  <p className="text-xs text-center text-slate-400 mt-1">
                    By submitting, you agree to receive a text message about your results at the number provided. Message and data rates may apply.
                  </p>
                </form>
              </Card>
              )
            ) : (
              /* Compact Results Card */
              <Card className="p-6 bg-white shadow-xl border-0">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4" style={{ backgroundColor: 'rgba(7, 146, 107, 0.1)' }}>
                    <CheckCircle className="w-4 h-4" style={{ color: CONFIG.brandGreen }} />
                    <span className="text-sm font-medium" style={{ color: CONFIG.brandGreen }}>Analysis Complete</span>
                  </div>
                  
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">
                    {result.company_name || companyName || "Your Company"}
                  </h2>

                  {/* Score Circle */}
                  <div className="mb-4">
                    <div className="relative w-28 h-28 mx-auto">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                        <circle
                          cx="50" cy="50" r="42" fill="none"
                          stroke={CONFIG.brandGreen}
                          strokeWidth="8" strokeLinecap="round"
                          strokeDasharray={`${result.final_score * 2.64} 264`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-bold text-slate-900">{result.final_score}</span>
                        <span className="text-xs text-slate-500">out of 100</span>
                      </div>
                    </div>
                    
                    <Badge className="text-white text-sm px-3 py-1 mt-2" style={{ backgroundColor: getScoreBand(result.final_score).color }}>
                      {getScoreBand(result.final_score).label}
                    </Badge>
                  </div>

                  {/* Company Overview */}
                  {result.company_summary && (
                    <div className="rounded-lg p-4 mb-4 text-left bg-slate-50">
                      <h3 className="text-sm font-semibold text-slate-900 mb-2">Company Overview</h3>
                      <p className="text-sm text-slate-600">{result.company_summary}</p>
                    </div>
                  )}

                  {/* Pillar Breakdown */}
                  {result.pillar_totals && result.pillar_totals.length > 0 && (
                    <div className="rounded-lg p-4 mb-4 text-left bg-slate-50">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Pillar Breakdown</h3>
                      <div className="space-y-3">
                        {result.pillar_totals.map((pillar, idx) => (
                          <div key={idx}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-slate-600">{pillar.name}</span>
                              <span className="font-medium" style={{ color: CONFIG.brandGreen }}>{pillar.total}/{pillar.max}</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full transition-all"
                                style={{ width: `${(pillar.total / pillar.max) * 100}%`, backgroundColor: CONFIG.brandGreen }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Prediction Comparison */}
                  {predictedScore !== null && (
                    <div className="rounded-lg p-3 mb-3 text-center" style={{ 
                      backgroundColor: Math.abs(result.final_score - predictedScore) <= 10 
                        ? 'rgba(7, 146, 107, 0.1)' 
                        : 'rgba(251, 191, 36, 0.1)' 
                    }}>
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <Trophy className="w-4 h-4" style={{ 
                          color: Math.abs(result.final_score - predictedScore) <= 10 ? CONFIG.brandGreen : '#f59e0b' 
                        }} />
                        <span className="text-xs font-semibold uppercase" style={{ 
                          color: Math.abs(result.final_score - predictedScore) <= 10 ? CONFIG.brandGreen : '#f59e0b' 
                        }}>
                          Your Prediction
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">
                        You predicted <strong>{predictedScore}</strong> — Actual: <strong>{result.final_score}</strong>
                        {Math.abs(result.final_score - predictedScore) <= 5 && " 🎯 Spot on!"}
                        {Math.abs(result.final_score - predictedScore) > 5 && Math.abs(result.final_score - predictedScore) <= 10 && " 👏 Close!"}
                        {Math.abs(result.final_score - predictedScore) > 10 && Math.abs(result.final_score - predictedScore) <= 20 && " 🤔 Not bad! Let's talk about the gap."}
                        {Math.abs(result.final_score - predictedScore) > 20 && " 😮 Surprising! Let's talk about the gap."}
                      </p>
                    </div>
                  )}

                  {/* Key Insights */}
                  {result.top_strengths?.[0] && (
                    <div className="rounded-lg p-3 mb-3 text-left" style={{ backgroundColor: 'rgba(7, 146, 107, 0.08)' }}>
                      <div className="flex items-center gap-2 mb-1" style={{ color: CONFIG.brandGreen }}>
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase">Key Strength</span>
                      </div>
                      <p className="text-sm text-slate-700">{result.top_strengths[0]}</p>
                    </div>
                  )}

                  {/* Opportunity */}
                  {result.top_gaps?.[0] && (
                    <div className="bg-amber-50 rounded-lg p-3 mb-3 text-left">
                      <div className="flex items-center gap-2 text-amber-700 mb-1">
                        <TrendingDown className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase">Opportunity</span>
                      </div>
                      <p className="text-sm text-slate-700">{result.top_gaps[0]}</p>
                    </div>
                  )}

                  {/* Strategy Alignment Insight */}
                  {result.alignment_insight && (
                    <div className="bg-blue-50 rounded-lg p-3 mb-3 text-left">
                      <div className="flex items-center gap-2 text-blue-700 mb-1">
                        <Compass className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase">Strategy Alignment Insight</span>
                      </div>
                      <p className="text-sm text-slate-700">{result.alignment_insight}</p>
                    </div>
                  )}

                  {/* Want the Full Report CTA */}
                  <div className="rounded-lg p-4 mt-4 bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
                    <Lock className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                    <h3 className="text-base font-semibold text-slate-900 mb-1">
                      Want the Full Report?
                    </h3>
                    <p className="text-xs text-slate-600 mb-3">
                      Get the complete 25-category breakdown with detailed recommendations.
                    </p>
                    <Button
                      className="w-full h-10 text-white font-medium"
                      style={{ backgroundColor: CONFIG.brandGreen }}
                      onClick={() => window.open(CONFIG.calendlyLink, "_blank")}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Book Your Strategy Call
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Minimal Footer */}
        <div className="bg-[#232326] py-3 text-center border-t border-[#333337]">
          <a href={CONFIG.companyWebsite} className="text-xs text-slate-500 hover:text-white" target="_blank" rel="noopener noreferrer">
            © {new Date().getFullYear()} {CONFIG.companyName}
          </a>
        </div>
      </div>
    );
  }

  // Full standalone page (non-embed)
  return (
    <div className="bg-[#232326] flex flex-col min-h-screen">
      {/* Hero Section with Green Accent */}
      <section className={`relative flex flex-col ${isInIframe ? '' : 'flex-grow'}`}>
        {/* Green accent bar */}
        <div className="h-2 w-full flex-shrink-0" style={{ backgroundColor: CONFIG.brandGreen }}></div>
        
        <div className={`bg-[#232326] flex flex-col ${isInIframe ? '' : 'flex-grow'}`}>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
          
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 relative z-10">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6" style={{ backgroundColor: 'rgba(7, 146, 107, 0.15)', border: '1px solid rgba(7, 146, 107, 0.3)' }}>
                <Sparkles className="w-4 h-4" style={{ color: CONFIG.brandGreen }} />
                <span className="text-sm font-medium" style={{ color: CONFIG.brandGreen }}>Free Assessment</span>
              </div>
              
              <h1 className="font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white" data-testid="public-score-title">
                Revenue Acceleration Score
              </h1>
              <p className="mt-4 text-base md:text-lg text-slate-300 max-w-2xl mx-auto" data-testid="public-score-subtitle">
                Get an instant AI-powered assessment of your go-to-market strategy. 
                Discover your strengths and opportunities in under 60 seconds.
              </p>
            </div>

            {/* Score Form or Results */}
            <div className="mt-10 max-w-lg mx-auto">
              {!result ? (
                loading && jobId ? (
                  <AnalysisProgress
                    companyName={companyName || "your website"}
                    pollingStatus={pollingStatus}
                    onPrediction={(score) => { setPredictedScore(score); predictedScoreRef.current = score; }}
                    startTime={analysisStartTime}
                  />
                ) : (
                <Card className="p-6 sm:p-8 bg-white shadow-2xl border-0" data-testid="public-score-form-card">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Website URL */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        <Globe className="w-4 h-4 inline mr-1.5 text-slate-400" />
                        Your Website URL
                      </label>
                      <Input
                        type="text"
                        placeholder="yourcompany.com"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        className="h-11"
                        data-testid="public-score-url-input"
                        disabled={loading}
                      />
                    </div>

                    {/* Work Email */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        <Mail className="w-4 h-4 inline mr-1.5 text-slate-400" />
                        Work Email <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`h-11 ${emailError ? "border-red-500 focus:ring-red-500" : ""}`}
                        data-testid="public-score-email-input"
                        disabled={loading}
                      />
                      {emailError && (
                        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {emailError}
                        </p>
                      )}
                    </div>

                    {/* Name and Company - Side by Side */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          <User className="w-4 h-4 inline mr-1.5 text-slate-400" />
                          Your Name
                        </label>
                        <Input
                          type="text"
                          placeholder="John Smith"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="h-11"
                          data-testid="public-score-name-input"
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          <Building2 className="w-4 h-4 inline mr-1.5 text-slate-400" />
                          Company
                        </label>
                        <Input
                          type="text"
                          placeholder="Acme Inc"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          className="h-11"
                          data-testid="public-score-company-input"
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {/* Phone Number - Required */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        <Phone className="w-4 h-4 inline mr-1.5 text-slate-400" />
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="tel"
                        required
                        placeholder="+1 (555) 123-4567"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={`h-11 ${phoneError ? "border-red-500 focus:ring-red-500" : ""}`}
                        data-testid="public-score-phone-input"
                        disabled={loading}
                      />
                      {phoneError && (
                        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {phoneError}
                        </p>
                      )}
                    </div>

                    {/* Optional GTM Alignment Questions */}
                    <div className="border-t border-slate-200 pt-4 mt-2">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            <Target className="w-4 h-4 inline mr-1.5 text-slate-400" />
                            Describe your ideal customer
                          </label>
                          <Textarea
                            placeholder="What characteristics would make them an instant 'yes' for your product or service?"
                            value={idealCustomer}
                            onChange={(e) => setIdealCustomer(e.target.value)}
                            className="min-h-[70px] text-sm resize-none"
                            data-testid="public-score-ideal-customer"
                            disabled={loading}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            <Sparkles className="w-4 h-4 inline mr-1.5 text-slate-400" />
                            Primary product or offer focus
                          </label>
                          <Textarea
                            placeholder="If you sell more than one thing, which product or offer do you want more customers choosing right now?"
                            value={primaryOffer}
                            onChange={(e) => setPrimaryOffer(e.target.value)}
                            className="min-h-[70px] text-sm resize-none"
                            data-testid="public-score-primary-offer"
                            disabled={loading}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            <Compass className="w-4 h-4 inline mr-1.5 text-slate-400" />
                            Current lead sources
                          </label>
                          <Textarea
                            placeholder="Where are most of your leads currently coming from?"
                            value={leadSources}
                            onChange={(e) => setLeadSources(e.target.value)}
                            className="min-h-[70px] text-sm resize-none"
                            data-testid="public-score-lead-sources"
                            disabled={loading}
                          />
                        </div>

                        <p className="text-xs text-slate-500 italic">
                          These questions are optional but help us provide deeper insights by comparing your internal GTM perspective with how your market positioning is perceived externally.
                        </p>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !!emailError || !!phoneError}
                      className="w-full h-12 text-white text-base font-medium transition-colors"
                      style={{ backgroundColor: CONFIG.brandGreen }}
                      data-testid="public-score-submit-button"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {getStatusMessage(pollingStatus)}
                        </>
                      ) : (
                        <>
                          Get My Free Score
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-slate-500 pt-2">
                      We analyze publicly available information. No login required.
                    </p>
                    <p className="text-xs text-center text-slate-400 mt-1">
                      This tool does deep research to ensure accuracy. Please allow up to 3 minutes for your score to be calculated. We will also send a copy of the results to the email you provided.
                    </p>
                    <p className="text-xs text-center text-slate-400 mt-1">
                      By submitting, you agree to receive a text message about your results at the number provided. Message and data rates may apply.
                    </p>
                  </form>
                </Card>
                )
              ) : (
                /* Results Card */
                <Card className="p-6 sm:p-8 bg-white shadow-2xl border-0" data-testid="public-score-result-card">
                  <div className="text-center">
                    {/* Success Badge */}
                    <div className="mb-6">
                      <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-4" style={{ backgroundColor: 'rgba(7, 146, 107, 0.1)' }}>
                        <CheckCircle className="w-4 h-4" style={{ color: CONFIG.brandGreen }} />
                        <span className="text-sm font-medium" style={{ color: CONFIG.brandGreen }}>Analysis Complete</span>
                      </div>
                      
                      <h2 className="text-xl font-semibold text-slate-900" data-testid="result-company-name">
                        {result.company_name || companyName || "Your Company"}
                      </h2>
                    </div>

                    {/* Score Circle */}
                    <div className="mb-6">
                      <div className="relative w-36 h-36 mx-auto">
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                          <circle
                            cx="50"
                            cy="50"
                            r="42"
                            fill="none"
                            stroke="#e2e8f0"
                            strokeWidth="8"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="42"
                            fill="none"
                            stroke={CONFIG.brandGreen}
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={`${result.final_score * 2.64} 264`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-4xl font-bold text-slate-900" data-testid="result-score">
                            {result.final_score}
                          </span>
                          <span className="text-xs text-slate-500">out of 100</span>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <Badge className="text-white text-sm px-3 py-1" style={{ backgroundColor: getScoreBand(result.final_score).color }}>
                          {getScoreBand(result.final_score).label}
                        </Badge>
                        <p className="text-sm text-slate-600 mt-2">
                          {getScoreBand(result.final_score).description}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{getPercentileText(result.final_score)}</p>
                      </div>
                    </div>

                    {/* Prediction Comparison */}
                    {predictedScore !== null && (
                      <div className="rounded-lg p-4 mb-6 text-center" style={{ 
                        backgroundColor: Math.abs(result.final_score - predictedScore) <= 10 
                          ? 'rgba(7, 146, 107, 0.1)' 
                          : 'rgba(251, 191, 36, 0.1)' 
                      }}>
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Trophy className="w-5 h-5" style={{ 
                            color: Math.abs(result.final_score - predictedScore) <= 10 ? CONFIG.brandGreen : '#f59e0b' 
                          }} />
                          <span className="text-sm font-semibold uppercase" style={{ 
                            color: Math.abs(result.final_score - predictedScore) <= 10 ? CONFIG.brandGreen : '#f59e0b' 
                          }}>
                            Your Prediction
                          </span>
                        </div>
                        <p className="text-base text-slate-700">
                          You predicted <strong>{predictedScore}</strong> — Actual: <strong>{result.final_score}</strong>
                        </p>
                        <p className="text-lg mt-1">
                          {Math.abs(result.final_score - predictedScore) <= 5 && "🎯 Spot on! You know your business well."}
                          {Math.abs(result.final_score - predictedScore) > 5 && Math.abs(result.final_score - predictedScore) <= 10 && "👏 Close! Pretty good self-awareness."}
                          {Math.abs(result.final_score - predictedScore) > 10 && Math.abs(result.final_score - predictedScore) <= 20 && "🤔 Not bad! Let's talk about the gap."}
                          {Math.abs(result.final_score - predictedScore) > 20 && result.final_score > predictedScore && "😮 You're doing better than you thought! Let's talk about the gap."}
                          {Math.abs(result.final_score - predictedScore) > 20 && result.final_score < predictedScore && "😮 Interesting! Let's talk about the gap."}
                        </p>
                      </div>
                    )}

                    {/* Pillar Breakdown */}
                    {result.pillar_totals && (
                      <div className="mb-6 text-left">
                        <h3 className="text-sm font-semibold text-slate-700 mb-3">Your GTM Pillars</h3>
                        <div className="space-y-3">
                          {result.pillar_totals.map((pillar, idx) => (
                            <div key={idx}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-slate-600">{pillar.name}</span>
                                <span className="text-slate-900 font-medium">{pillar.total}/{pillar.max}</span>
                              </div>
                              <div className="w-full bg-slate-200 rounded-full h-2">
                                <div 
                                  className="h-2 rounded-full transition-all" 
                                  style={{ 
                                    width: `${(pillar.total / pillar.max) * 100}%`,
                                    backgroundColor: CONFIG.brandGreen 
                                  }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Key Insights */}
                    <div className="grid grid-cols-1 gap-3 mb-6 text-left">
                      {result.top_strengths?.[0] && (
                        <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(7, 146, 107, 0.08)' }}>
                          <div className="flex items-center gap-2 mb-1" style={{ color: CONFIG.brandGreen }}>
                            <TrendingUp className="w-4 h-4" />
                            <span className="text-xs font-semibold uppercase">Key Strength</span>
                          </div>
                          <p className="text-sm text-slate-700">{result.top_strengths[0]}</p>
                        </div>
                      )}
                      {result.top_gaps?.[0] && (
                        <div className="bg-amber-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-amber-700 mb-1">
                            <TrendingDown className="w-4 h-4" />
                            <span className="text-xs font-semibold uppercase">Opportunity</span>
                          </div>
                          <p className="text-sm text-slate-700">{result.top_gaps[0]}</p>
                        </div>
                      )}
                      {result.alignment_insight && (
                        <div className="bg-blue-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-blue-700 mb-1">
                            <Compass className="w-4 h-4" />
                            <span className="text-xs font-semibold uppercase">Strategy Alignment Insight</span>
                          </div>
                          <p className="text-sm text-slate-700">{result.alignment_insight}</p>
                        </div>
                      )}
                    </div>

                    {/* Locked Content */}
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-6 text-left">
                      <div className="flex items-center gap-2 text-slate-600 mb-2">
                        <Lock className="w-4 h-4" />
                        <span className="font-medium text-sm">Full Report Includes</span>
                      </div>
                      <ul className="text-sm text-slate-600 space-y-1.5">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-slate-400" />
                          Detailed analysis across all 25 categories
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-slate-400" />
                          Specific recommendations for your business
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-slate-400" />
                          Competitive benchmarking insights
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-slate-400" />
                          Actionable 90-day improvement roadmap
                        </li>
                      </ul>
                    </div>

                    {/* CTA */}
                    <Button
                      className="w-full h-12 text-white text-base font-medium"
                      style={{ backgroundColor: CONFIG.brandGreen }}
                      data-testid="book-call-button"
                      onClick={() => window.open(CONFIG.calendlyLink, "_blank")}
                    >
                      <Calendar className="w-5 h-5 mr-2" />
                      Book Your Free Strategy Call
                    </Button>
                    
                    <p className="text-xs text-slate-500 mt-3">
                      Get your complete Revenue Acceleration Score analysis with personalized recommendations.
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 bg-[#2c2c30] border-t border-[#3a3a3f]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-white mb-1">25</div>
              <div className="text-xs sm:text-sm text-slate-400">Categories Analyzed</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-white mb-1">5</div>
              <div className="text-xs sm:text-sm text-slate-400">Strategic Pillars</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-white mb-1">60s</div>
              <div className="text-xs sm:text-sm text-slate-400">Analysis Time</div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Analyze */}
      <section className="py-10 bg-[#232326]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-semibold text-white text-center mb-8">
            What We Analyze
          </h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {[
              { name: "Messaging", icon: Target, desc: "Clarity & positioning" },
              { name: "Lead Gen", icon: TrendingUp, desc: "Acquisition channels" },
              { name: "Sales", icon: Building2, desc: "Process & structure" },
              { name: "Content", icon: BarChart3, desc: "Thought leadership" },
              { name: "Tech Stack", icon: Globe, desc: "Tools & automation" },
            ].map((pillar, idx) => (
              <Card key={idx} className="p-4 bg-[#2c2c30] border-[#3a3a3f] text-center hover:bg-[#333337] transition-colors">
                <pillar.icon className="w-6 h-6 mx-auto mb-2" style={{ color: CONFIG.brandGreen }} />
                <div className="text-sm font-medium text-white">{pillar.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">{pillar.desc}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-[#1c1c1e] border-t border-[#333337]">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <a href={CONFIG.companyWebsite} className="text-sm text-slate-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">
            © {new Date().getFullYear()} {CONFIG.companyName}. All rights reserved.
          </a>
        </div>
      </footer>
    </div>
  );
}
